import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Images, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import * as Dialog from '@radix-ui/react-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 12;

export function GalleryPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [retry, setRetry] = useState(0);
  const pendingIndex = useRef(null);
  const touchStart = useRef(null);
  const opener = useRef(null);
  const it = language === 'it';
  const lightboxImage = selectedIndex === null ? null : images[selectedIndex];

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    axios.get(`${API}/gallery`, {
      params: { page, page_size: PAGE_SIZE, category: selectedCategory === 'all' ? undefined : selectedCategory },
      signal: controller.signal,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      setImages(data.items);
      setTotal(data.total);
      setPages(data.pages);
      setPage(data.page);
      if (pendingIndex.current !== null) {
        setSelectedIndex(data.items.length === 0 ? null : pendingIndex.current === -1 ? data.items.length - 1 : 0);
        pendingIndex.current = null;
      }
    }).catch(() => {
      if (!controller.signal.aborted) setError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [page, selectedCategory, retry]);

  const changePage = (nextPage) => {
    setLoading(true);
    setPage(nextPage);
  };
  const navigate = (direction) => {
    if (loading || error || selectedIndex === null) return;
    const nextIndex = selectedIndex + direction;
    if (nextIndex >= 0 && nextIndex < images.length) {
      setSelectedIndex(nextIndex);
    } else if (page + direction >= 1 && page + direction <= pages) {
      pendingIndex.current = direction === 1 ? 0 : -1;
      changePage(page + direction);
    }
  };
  const closeLightbox = () => {
    setSelectedIndex(null);
    pendingIndex.current = null;
  };
  const categories = ['all', 'climbing', 'crags', 'events', 'landscape'];
  const previousDisabled = loading || error || (page === 1 && selectedIndex === 0);
  const nextDisabled = loading || error || (page === pages && selectedIndex === images.length - 1);
  const errorMessage = (
    <div role="alert" className="text-center py-8">
      <p>{it ? 'Impossibile caricare le foto.' : 'Unable to load photos.'}</p>
      <button className="underline mt-3" onClick={() => { setLoading(true); setRetry(value => value + 1); }}>
        {it ? 'Riprova' : 'Try again'}
      </button>
    </div>
  );

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Images className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('gallery')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Immagini dalle nostre falesie e attività.'
                : 'Images from our crags and activities.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { setLoading(true); setSelectedCategory(cat); setPage(1); closeLightbox(); }}
                disabled={selectedCategory === cat}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-[#FF6D00] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? (language === 'it' ? 'Tutti' : 'All') : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
            </div>
          ) : error ? errorMessage : images.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">{t('noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, i) => (
                <motion.button
                  type="button"
                  aria-label={getTranslatedContent(image.caption) || `${it ? 'Apri foto' : 'Open photo'} ${(page - 1) * PAGE_SIZE + i + 1}`}
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="aspect-square overflow-hidden rounded-lg cursor-pointer group"
                  onClick={(event) => { opener.current = event.currentTarget; setSelectedIndex(i); }}
                >
                  <img 
                    loading="lazy"
                    decoding="async"
                    src={image.url}
                    alt={getTranslatedContent(image.caption)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </motion.button>
              ))}
            </div>
          )}
          {pages > 1 && (
            <nav aria-label={it ? 'Pagine della galleria' : 'Gallery pages'} className="flex flex-wrap items-center justify-center gap-4 mt-8">
              <button disabled={loading || page <= 1} onClick={() => changePage(page - 1)} className="px-4 py-2 rounded bg-gray-100 disabled:opacity-40">
                {it ? 'Precedente' : 'Previous'}
              </button>
              <label className="flex items-center gap-2">
                {it ? 'Pagina' : 'Page'}
                <select
                  aria-label={it ? 'Vai alla pagina' : 'Go to page'}
                  value={page}
                  disabled={loading}
                  onChange={(event) => changePage(Number(event.target.value))}
                  className="rounded border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF6D00] disabled:opacity-40"
                >
                  {Array.from({ length: pages }, (_, index) => index + 1).map(number => (
                    <option key={number} value={number}>{number}</option>
                  ))}
                </select>
                <span>/ {pages}</span>
              </label>
              <button disabled={loading || page >= pages} onClick={() => changePage(page + 1)} className="px-4 py-2 rounded bg-gray-100 disabled:opacity-40">
                {it ? 'Successiva' : 'Next'}
              </button>
            </nav>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog.Root open={selectedIndex !== null} onOpenChange={(open) => { if (!open) closeLightbox(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/95" />
          <Dialog.Content
            aria-describedby={undefined}
            onCloseAutoFocus={(event) => { event.preventDefault(); if (opener.current?.isConnected) opener.current.focus(); else document.querySelector('[data-testid="gallery-page"] button:not(:disabled)')?.focus(); }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') { event.preventDefault(); navigate(-1); }
              if (event.key === 'ArrowRight') { event.preventDefault(); navigate(1); }
            }}
            className="fixed inset-x-2 top-1/2 -translate-y-1/2 z-50 text-white outline-none sm:inset-x-8"
          >
            <Dialog.Title className="sr-only">{t('gallery')}</Dialog.Title>
            <Dialog.Close aria-label={it ? 'Chiudi' : 'Close'} className="absolute right-2 top-0 z-10 p-3 bg-black/60 rounded-full">
              <X className="w-7 h-7" />
            </Dialog.Close>
            <div className="min-h-[50vh] flex flex-col items-center justify-center px-12"
              onTouchStart={(event) => { touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }; }}
              onTouchCancel={() => { touchStart.current = null; }}
              onTouchEnd={(event) => {
                if (!touchStart.current) return;
                const dx = event.changedTouches[0].clientX - touchStart.current.x;
                const dy = event.changedTouches[0].clientY - touchStart.current.y;
                touchStart.current = null;
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) navigate(dx < 0 ? 1 : -1);
              }}>
              {loading ? <p role="status">{it ? 'Caricamento…' : 'Loading…'}</p> : error ? errorMessage : lightboxImage && (
                <>
                  <img src={lightboxImage.url} alt={getTranslatedContent(lightboxImage.caption)} className="max-w-full max-h-[75vh] object-contain" />
                  <p className="text-center mt-4">{getTranslatedContent(lightboxImage.caption)}</p>
                  <p className="text-center text-sm text-gray-300 mt-2" aria-live="polite">{(page - 1) * PAGE_SIZE + selectedIndex + 1} / {total}</p>
                </>
              )}
            </div>
            <button aria-label={it ? 'Foto precedente' : 'Previous photo'} disabled={previousDisabled} onClick={() => navigate(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 p-2 bg-black/60 rounded-full disabled:opacity-30">
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button aria-label={it ? 'Foto successiva' : 'Next photo'} disabled={nextDisabled} onClick={() => navigate(1)} className="absolute right-0 top-1/2 -translate-y-1/2 p-2 bg-black/60 rounded-full disabled:opacity-30">
              <ChevronRight className="w-8 h-8" />
            </button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
